"use client";

import { useEffect, useRef, useState } from "react";
import {
  platform,
  type DesignInvitation,
  type DesignInvitationStatus,
} from "./api";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "./ui";
import css from "./BuildyWorkspace.module.css";

export function WebsiteCustomerInvitation({
  websiteId,
  org,
  csrf,
}: {
  websiteId: string;
  org: number;
  csrf: string;
}) {
  const [record, setRecord] = useState<DesignInvitationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [retry, setRetry] = useState(0);
  const [clock, setClock] = useState(Date.now());
  const lock = useRef(false);
  const requestKey = useRef<string | null>(null);
  const base = `websites/${websiteId}/customer-invitation/`;
  const invitation = record?.invitation;
  const cooldown = invitation
    ? new Date(invitation.created_at).getTime() + 60_000 - clock
    : 0;
  const expired = invitation
    ? new Date(invitation.expires_at).getTime() <= clock
    : false;
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function poll() {
      try {
        const value = await platform<DesignInvitationStatus>(base, {
          org,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setRecord(value);
        setError("");
        setClock(Date.now());
        if (
          value.invitation?.status === "PENDING" &&
          new Date(value.invitation.created_at).getTime() + 60_000 > Date.now()
        )
          timer = setTimeout(poll, 4000);
      } catch (cause) {
        if (!controller.signal.aborted)
          setError(
            cause instanceof Error
              ? cause.message
              : "Could not load invitation status.",
          );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    setLoading(true);
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [base, org, retry]);
  useEffect(() => {
    if (!invitation) return;
    const next = Math.min(
      ...[
        new Date(invitation.created_at).getTime() + 60_000,
        new Date(invitation.expires_at).getTime(),
      ].filter((value) => value > Date.now()),
    );
    if (!Number.isFinite(next)) return;
    const timer = setTimeout(
      () => setClock(Date.now()),
      next - Date.now() + 20,
    );
    return () => clearTimeout(timer);
  }, [invitation, clock]);
  async function send() {
    if (!record || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    requestKey.current ??= crypto.randomUUID();
    try {
      const value = await platform<DesignInvitation>(base, {
        method: "POST",
        org,
        csrf,
        body: { request_key: requestKey.current },
      });
      setRecord({ ...record, invitation: value });
      setClock(Date.now());
      requestKey.current = null;
      setConfirming(false);
      if (value.status === "PENDING") setRetry((value) => value + 1);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not confirm invitation delivery. Retry to check the same request.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <div>
      <h3>Invitation</h3>
      {loading ? (
        <p role="status">Checking invitation status…</p>
      ) : (
        record && (
          <>
            <p>
              {invitation?.status === "ACCEPTED"
                ? "The customer has accepted their invitation."
                : invitation?.status === "SENT" && !expired
                  ? `Invitation sent to ${invitation.recipient_email}.`
                  : invitation?.status === "FAILED"
                    ? "Invitation delivery could not be confirmed."
                    : invitation?.status === "PENDING"
                      ? "Invitation delivery is not yet confirmed."
                      : expired
                        ? "The invitation has expired."
                        : "The customer has not been invited yet."}
            </p>
            {invitation?.status !== "ACCEPTED" && (
              <>
                <p>Send secure account access to {record.recipient_email}</p>
                <Button
                  disabled={busy || cooldown > 0}
                  onClick={() =>
                    invitation && !requestKey.current
                      ? setConfirming(true)
                      : void send()
                  }
                >
                  {busy
                    ? "Sending invitation…"
                    : requestKey.current
                      ? "Retry invitation request"
                      : invitation
                        ? "Send replacement invitation"
                        : "Send invitation"}
                </Button>
              </>
            )}
          </>
        )
      )}
      {error && (
        <p role="alert" className={css.error}>
          {error}
        </p>
      )}
      {!loading && !record && (
        <Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>
      )}
      {confirming && (
        <Dialog
          ariaLabel="Send replacement invitation"
          onClose={() => {
            if (!busy) setConfirming(false);
          }}
          closeOnEscape={!busy}
        >
          <DialogHeader>
            <DialogTitle>Send a replacement invitation?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p>
              This sends a new link to {record?.recipient_email}. The previous
              invitation link will stop working.
            </p>
            {error && (
              <p role="alert" className={css.error}>
                {error}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => void send()}
            >
              {busy ? "Sending…" : "Send replacement"}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  );
}
